import React, { useEffect, useMemo, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Box, Paper, Typography } from "@mui/material";
import { useLocale, useMessage } from "../i18n/locale";
import {
  getMappableItems,
  SOUTH_AFRICA_ROAD_STYLE,
  SOUTH_AFRICA_MAP_VIEW,
} from "./discoveryMap.utils";

const MAP_STYLE =
  process.env.REACT_APP_MAP_STYLE_URL || SOUTH_AFRICA_ROAD_STYLE;

export default function DiscoveryMap({
  items,
  getCoordinates,
  getLabel,
  onSelect,
  labelMessageKey = "discoveryMapLabel",
  height = 360,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const { direction } = useLocale();
  const mapLabel = useMessage(labelMessageKey);
  const emptyMessage = useMessage("discoveryMapEmpty");
  const mappableItems = useMemo(
    () => getMappableItems(items, getCoordinates),
    [getCoordinates, items]
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const controlSide = direction === "rtl" ? "left" : "right";
    const initialView =
      mappableItems.length > 0
        ? { center: mappableItems[0].coordinates, zoom: 11 }
        : SOUTH_AFRICA_MAP_VIEW;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      ...initialView,
      attributionControl: false,
    });

    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      `top-${controlSide}`
    );
    map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      `bottom-${controlSide}`
    );
    mapRef.current = map;

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [direction, mappableItems]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = mappableItems.map(({ item, coordinates }) => {
      const markerElement = document.createElement("button");
      markerElement.type = "button";
      markerElement.className = "bandjoe-map-marker";
      markerElement.setAttribute("aria-label", getLabel(item));
      markerElement.addEventListener("click", () => onSelect?.(item));

      return new maplibregl.Marker({
        element: markerElement,
        anchor: "bottom-left",
      })
        .setLngLat(coordinates)
        .addTo(map);
    });

    if (mappableItems.length === 1) {
      map.easeTo({ center: mappableItems[0].coordinates, zoom: 12 });
    } else if (mappableItems.length > 1) {
      const bounds = new maplibregl.LngLatBounds();
      mappableItems.forEach(({ coordinates }) => bounds.extend(coordinates));
      map.fitBounds(bounds, { padding: 56, maxZoom: 13, duration: 0 });
    }
  }, [getLabel, mappableItems, onSelect]);

  return (
    <Paper
      variant="outlined"
      sx={{ height, borderRadius: 3, overflow: "hidden", position: "relative" }}
    >
      <Box
        ref={containerRef}
        role="region"
        aria-label={mapLabel}
        dir={direction}
        sx={{
          height: "100%",
          "& .maplibregl-ctrl-top-right": {
            insetInlineEnd: 0,
            insetInlineStart: "auto",
          },
        }}
      />
      {mappableItems.length === 0 && (
        <Typography
          variant="body2"
          role="status"
          sx={{
            position: "absolute",
            insetInlineStart: "50%",
            bottom: 2,
            transform: "translateX(-50%)",
            maxWidth: "calc(100% - 32px)",
            borderRadius: 2,
            bgcolor: "background.paper",
            color: "text.secondary",
            boxShadow: 2,
            px: 2,
            py: 1,
            textAlign: "center",
          }}
        >
          {emptyMessage}
        </Typography>
      )}
    </Paper>
  );
}
